import React from 'react';
import { Share2, Check } from 'lucide-react';
import { useShare } from '../hooks/useShare';
import { useIsPublicWorldCup } from '../hooks/usePublicWorldCupConfig';

interface Competition {
  id: string;
  name: string;
  competition_type?: string | null;
  competitionType?: string;
}

interface ShareButtonProps {
  competition: Competition;
  organizationSlug: string;
  variant?: 'icon' | 'button';
  className?: string;
  size?: 'sm' | 'md';
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  competition,
  organizationSlug,
  variant = 'icon',
  className = '',
  size = 'sm'
}) => {
  const { share, copied } = useShare();
  const isPublicWorldCup = useIsPublicWorldCup(competition.id);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const competitionType = competition.competition_type || competition.competitionType || 'lms';
    const baseUrl = window.location.origin;
    // Use the short, brandable /worldcup URL for the configured public WC.
    const shareUrl = isPublicWorldCup
      ? `${baseUrl}/worldcup`
      : `${baseUrl}/${organizationSlug}/${competitionType}?comp=${competition.id}`;
    
    await share({
      title: competition.name,
      text: `Join me in ${competition.name}!`,
      url: shareUrl
    });
  };
  
  const iconSize = size === 'sm' ? 14 : 18;
  const buttonPadding = size === 'sm' ? 'p-1.5' : 'p-2';
  
  if (variant === 'icon') {
    return (
      <button
        onClick={handleShare}
        className={`
          ${buttonPadding} rounded-full
          bg-gray-800/10 hover:bg-gray-800/20 
          dark:bg-white/10 dark:hover:bg-white/20
          backdrop-blur-sm
          transition-all duration-200
          hover:scale-105 active:scale-95
          ${className}
        `}
        title={copied ? 'Link copied!' : 'Share competition'}
        aria-label={copied ? 'Link copied!' : 'Share competition'}
      >
        {copied ? (
          <Check size={iconSize} className="text-green-600 dark:text-green-400" />
        ) : (
          <Share2 size={iconSize} className="text-gray-700 dark:text-white/80" />
        )}
      </button>
    );
  }
  
  return (
    <button
      onClick={handleShare}
      className={`
        flex items-center gap-2 px-3 py-1.5 rounded-lg
        bg-gray-800/10 hover:bg-gray-800/20 
        dark:bg-white/10 dark:hover:bg-white/20
        backdrop-blur-sm
        transition-all duration-200
        text-sm font-medium text-gray-700 dark:text-white/90
        hover:scale-105 active:scale-95
        ${className}
      `}
    >
      {copied ? (
        <>
          <Check size={iconSize} className="text-green-600 dark:text-green-400" />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Share2 size={iconSize} />
          <span>Share via...</span>
        </>
      )}
    </button>
  );
};
